import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { Stack, Box } from "@mui/material";
import useMaps from "../../../hooks/useMaps";
import { AuthContext } from "../../../Auth/Context";
import { socketWex } from "../../../utils/Socket";
import MapDisplay from "../../../components/atoms/MapDisplay";
import InputComponent from "../../../components/atoms/InputComponent";
import ButtonComponent from "../../../components/atoms/ButtonComponent";
import InputCheckboxes from "../../../components/atoms/InputCheckboxes";
import { usePermissions } from "../../../hooks/usePermissions";
import ConnectionMonitorTerminal from "../../../components/molecules/ConnectionMonitorTerminal";
import ConnectionMonitorWidget from "../../../components/molecules/ConnectionMonitorWidget";
import CabinFormModal from "../../../components/molecules/CabinFormModal";
import useWexClient from "../../../hooks/Clients/wexClient,";

const DEFAULT_AGENT = "00001";

function RealTimeMaps({
  selectedAgent = "",
  nameAgent = [],
  setSelectedAgent = () => {},
  users = [],
  selectedRegionId = null,
  onIncidentCreated = () => {},
  onIncidentLocationChange = () => {},
  permissions = {},
}) {
  const [receive, setReceive] = useState("Real-Time");
  const [data, setData] = useState([]);
  const { payload } = useContext(AuthContext);
  const { can } = usePermissions(payload);
  const canSeeMonitor = can("monitor-socket");
  const canCreateIncident = permissions?.incidents?.create === true;

  const { getRouteByPoints, loader, error } = useMaps();

  // ESTADOS PARA BÚSQUEDA DE INCIDENTE
  const [searchIncident, setSearchIncident] = useState("");
  const [incidentLocation, setIncidentLocation] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [showTraffic, setShowTraffic] = useState(false);
  const [isIncidentModalOpen, setIsIncidentModalOpen] = useState(false);
  const [incidentModalData, setIncidentModalData] = useState(null);
  const [adjusters, setAdjusters] = useState([]); // ajustadores disponibles para el modal
  const [incidentFormState, setIncidentFormState] = useState({
    form: {
      name: "",
      lastName: "",
      phone: "",
      folio: "",
      reference: "",
      comments: "",
    },
    agent: "00001",
    agentConfirmed: false,
    sendNotification: true,
    adjusterWhatsappMessage: false,
  });

  const [trafficOffset, setTrafficOffset] = useState(0);
  const { createForms, geocodingSearch } = useWexClient();

  useEffect(() => {
    onIncidentLocationChange?.(incidentLocation);
  }, [incidentLocation, onIncidentLocationChange]);

  const normalizeAgents = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.agents)) return data.agents;
    return [];
  };

  const handleAgentsConnected = (data) => {
    const safeData = normalizeAgents(data);

    const listAgents = safeData
      .filter((item) => item?.status === "Disponible")
      .map((item) => ({
        label:
          item?.agent?.profile?.alias ||
          item?.agent?.user ||
          item?.label ||
          "Sin nombre",
        value: item?.agent?._id || item?.value || item?._id,
        agent: item?.agent || item,
      }))
      .filter((item) => item.value);

    setAdjusters([
      { label: "Buscar ajustador...", value: DEFAULT_AGENT, agent: null },
      ...listAgents,
    ]);
  };

  const updateAgentStatus = (agentId, updates) => {
    if (!agentId) return;

    setData((prev) =>
      prev.map((agent) => {
        if (String(agent.userId) !== String(agentId)) return agent;

        return {
          ...agent,
          ...updates,
          preselected: updates.preselected ?? agent.preselected ?? false,
          waiting_decision:
            updates.waiting_decision ?? agent.waiting_decision ?? false,
          busy: updates.busy ?? agent.busy ?? false,
        };
      }),
    );
  };

  const getAgentIdFromEvent = (payload) => {
    const candidates = [
      payload?.agent?._id,
      payload?.agentId,
      payload?.idAgent,
      payload?.incident?.header?.agent?._id,
      payload?.incident?.agent?._id,
      payload?.data?.agent?._id,
      payload?.data?.idAgent,
      payload?.agent_id,
    ];

    return candidates.find(Boolean);
  };

  // Sockets monitoreo
  const [isConnected, setIsConnected] = useState(socketWex.connected);
  const [lastHeartbeat, setLastHeartbeat] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const clockTimer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const handleConnect = () => {
      setIsConnected(true);
      setLastHeartbeat(new Date());
    };
    const handleDisconnect = () => {
      setIsConnected(false);
    };

    socketWex.on("connect", handleConnect);
    socketWex.on("disconnect", handleDisconnect);

    if (socketWex.connected) {
      handleConnect();
    }

    const activityEvents = [
      "data-adjusters",
      "cabin-message-inbound",
      "new-whatsapp-inbound",
      "cabin-cancel-incidents",
      "adjuster-reject-incident",
      "adjuster-reject-for-timeout",
      "adjuster-accept-incident",
      "close-incident",
      "agent-already-taken",
      "agent-removed",
    ];

    const handleActivity = () => {
      setLastHeartbeat(new Date());
    };

    activityEvents.forEach((event) => {
      socketWex.on(event, handleActivity);
    });

    let pingStartTime = 0;
    const handlePing = () => {
      pingStartTime = Date.now();
    };
    const handlePong = () => {
      setLastHeartbeat(new Date());
    };

    const engine = socketWex.io?.engine;
    if (engine) {
      engine.on("ping", handlePing);
      engine.on("pong", handlePong);
    }

    return () => {
      clearInterval(clockTimer);
      socketWex.off("connect", handleConnect);
      socketWex.off("disconnect", handleDisconnect);
      activityEvents.forEach((event) => {
        socketWex.off(event, handleActivity);
      });
      if (engine) {
        engine.off("ping", handlePing);
        engine.off("pong", handlePong);
      }
    };
  }, []);

  // Distancia inicial que devuelve OSRM
  const [initialDistance, setInitialDistance] = useState(0);
  const [initialOSRMDuration, setInitialOSRMDuration] = useState(0);

  // Última ubicación del ajustador consultada
  const lastAdjusterLocationRef = useRef(null);

  // Evita consultar OSRM demasiado seguido
  const lastOSRMRequestRef = useRef(0);

  const normalizeAgent = (item = {}) => {
    const userCode = item.userCode ?? item.profile?.userCode;
    const rawUser = item.user ?? item.nameUser ?? item.agent?.user ?? item.name;

    const userString =
      typeof rawUser === "object"
        ? rawUser.user || rawUser.name || "Desconocido"
        : rawUser || "Desconocido";

    const normalized = {
      userId: item.userId ?? item.idUser ?? item._id ?? item.agent?._id ?? null,
      user: userString,
      name: userCode || userString,
      userCode: userCode || null,
      userLocation:
        item.userLocation ??
        item.newLocation ??
        item.location ??
        item.agent?.location ??
        null,
      locationDt: item.locationDt ?? item.agent?.locationDt ?? null,
    };

    if (typeof item.waiting_decision === "boolean")
      normalized.waiting_decision = item.waiting_decision;
    if (typeof item.busy === "boolean") normalized.busy = item.busy;
    if (typeof item.preselected === "boolean")
      normalized.preselected = item.preselected;

    if (normalized.busy === true) {
      normalized.preselected = false;
      normalized.waiting_decision = false;
    } else if (normalized.waiting_decision === true) {
      normalized.preselected = false;
      normalized.busy = false;
    }

    return normalized;
  };

  const normalize = (incoming) => {
    if (Array.isArray(incoming)) {
      return incoming.map(normalizeAgent).filter((x) => x.userId);
    }

    if (incoming && typeof incoming === "object") {
      const normalized = normalizeAgent(incoming);
      return normalized.userId ? [normalized] : [];
    }

    return [];
  };

  const upsertByUserId = (prev, updates) => {
    const map = new Map();

    prev.forEach((item) => {
      const normalized = normalizeAgent(item);
      if (normalized.userId) {
        map.set(String(normalized.userId), normalized);
      }
    });

    updates.forEach((item) => {
      const normalized = normalizeAgent(item);
      if (!normalized.userId) return;
      const key = String(normalized.userId);
      map.set(key, {
        ...(map.get(key) || {}),
        ...normalized,
      });
    });

    return [...map.values()];
  };

  const mergeAgentData = (locations, agents) => {
    if (!agents || agents.length === 0) return locations;

    return locations.map((location) => {
      const agentData = agents.find(
        (a) => String(a.agent?._id) === String(location.userId),
      );

      if (!agentData) return location;

      return {
        ...location,
        user: agentData.agent?.user || location.user,
        name:
          agentData.agent?.userCode ||
          agentData.agent?.profile?.userCode ||
          agentData.agent?.user ||
          location.name,
        locationDt: location.locationDt || agentData.agent?.locationDt,
      };
    });
  };

  const memoizedRegionIds = useMemo(() => {
    if (!selectedRegionId) return [];
    return Array.isArray(selectedRegionId)
      ? selectedRegionId
      : [selectedRegionId];
  }, [selectedRegionId]);

  useEffect(() => {
    if (!payload?._id || memoizedRegionIds.length === 0) {
      setAdjusters([
        { label: "Buscar ajustador...", value: DEFAULT_AGENT, agent: null },
      ]);
      return;
    }

    // Solicita los ajustadores disponibles para la región/servicio activo
    const body = {
      idService: payload?.service || payload?.services?.[0],
      regionIds: memoizedRegionIds,
    };

    socketWex.emit("get-adjusters", body);
    socketWex.on("data-adjusters", handleAgentsConnected);
    socketWex.on("agent-removed", handleAgentsConnected);

    return () => {
      socketWex.off("data-adjusters", handleAgentsConnected);
      socketWex.off("agent-removed", handleAgentsConnected);
    };
  }, [payload?._id, payload?.service, memoizedRegionIds]);

  useEffect(() => {
    if (receive !== "Real-Time" || memoizedRegionIds.length === 0) {
      setData([]);
      return;
    }

    setData([]);
    socketWex.emit("supervisor-connected", memoizedRegionIds);

    const onRegistered = (serverData) => {
      //console.log("registered-supervisor", serverData);
      const normalized = normalize(serverData?.ubications ?? []);
      const base = mergeAgentData(normalized, nameAgent);
      setData(base);
    };

    const onUpdate = (evt) => {
      //console.log("update-location", evt);
      const updates = normalize(evt);
      //console.log("UPDATE NORMALIZADO", updates);
      const merged = mergeAgentData(updates, nameAgent);
      setData((prev) => {
        const result = upsertByUserId(prev, merged);

        //console.log(
        //  "Agente despues del Merge",
        //  result.find((x) => x.userId === "698127b174b06a250cfc8f19"),
        //);
        return result;
      });
    };

    const handleSelectAdjuster = (data) => {
      const agentId = data?._id || data?.agent?._id;
      if (!agentId) return;

      updateAgentStatus(agentId, {
        preselected: Boolean(data?.preselected),
        waiting_decision: false,
        busy: false,
      });
    };

    const handleWaitingDecision = (data) => {
      const agentId = getAgentIdFromEvent(data);
      if (!agentId) return;

      updateAgentStatus(agentId, {
        preselected: false,
        waiting_decision: true,
        busy: false,
      });
    };

    const handleAcceptIncident = (data) => {
      const agentId = getAgentIdFromEvent(data);
      if (!agentId) return;

      updateAgentStatus(agentId, {
        preselected: false,
        waiting_decision: false,
        busy: true,
      });
    };

    const handleRejectIncident = (data) => {
      const agentId = getAgentIdFromEvent(data);
      if (!agentId) return;

      updateAgentStatus(agentId, {
        preselected: false,
        waiting_decision: false,
        busy: false,
      });
    };

    const handleAgentsConnected = (data) => {
      //console.log("data-adjusters", data);

      const safeData = normalizeAgents(data);

      const listAgents = safeData
        .filter((item) => item.status === "Disponible")
        .map((item) => ({
          label: item.agent.profile?.alias || item.agent?.user || "Sin nombre",
          value: item.agent._id,
          agent: item.agent,
        }));

      const agentsWithDefault = [
        { label: "Buscar ajustador...", value: DEFAULT_AGENT, agent: null },
        ...listAgents,
      ];

      setAdjusters(agentsWithDefault);
    };

    socketWex.on("agent-preselected", handleSelectAdjuster);
    socketWex.on("adjuster-accept-incident", handleAcceptIncident);
    socketWex.on("adjuster-reject-incident", handleRejectIncident);
    socketWex.on("adjuster-reject-for-timeout", handleRejectIncident);
    socketWex.on("registered-supervisor", onRegistered);
    socketWex.on("update-location", onUpdate);
    socketWex.on("data-adjusters", handleAgentsConnected);

    return () => {
      socketWex.off("agent-preselected", handleSelectAdjuster);
      socketWex.off("adjuster-accept-incident", handleAcceptIncident);
      socketWex.off("adjuster-reject-incident", handleRejectIncident);
      socketWex.off("adjuster-reject-for-timeout", handleRejectIncident);
      socketWex.off("registered-supervisor", onRegistered);
      socketWex.off("update-location", onUpdate);
      socketWex.emit("supervisor-disconnected");
    };
  }, [receive, memoizedRegionIds, nameAgent]);

  const filteredData = data.filter((item) => {
    const isSelected =
      !selectedAgent ||
      selectedAgent === "00001" ||
      String(item.userId).trim() === String(selectedAgent).trim();

    return isSelected && item.userLocation;
  });

  const isCoordinates = (query) => {
    const regex = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/;
    return regex.test(query);
  };

  const parseLocation = (userLocation) => {
    if (!userLocation) return null;
    let lat, lng;
    if (typeof userLocation === "string") {
      const parts = userLocation.split(",");
      lat = Number(parts[0]);
      lng = Number(parts[1]);
    } else if (typeof userLocation === "object") {
      lat = Number(userLocation.lat);
      lng = Number(userLocation.lng);
    }
    if (isNaN(lat) || isNaN(lng)) return null;
    return { lat, lng };
  };

  const formatDuration = (seconds) => {
    const totalMinutes = Math.round(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours <= 0) {
      return `${minutes} min`;
    }
    return `${hours} h ${minutes} min`;
  };

  const formatDistance = (meters) => {
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const calculateRemainingOffset = (
    currentDistance,
    initialDistance,
    initialOffset,
  ) => {
    if (!initialDistance) return initialOffset;
    const ratio = Math.min(1, Math.max(0, currentDistance / initialDistance));
    return initialOffset * ratio;
  };

  const mapCoordinates = (geometry) => {
    return geometry.coordinates.map((coord) => ({
      lat: coord[1],
      lng: coord[0],
    }));
  };

  const handleSearchIncident = async () => {
    try {
      setSelectedRoute(null);
      setRouteInfo(null);
      setTrafficOffset(0);
      setInitialOSRMDuration(0);

      if (isCoordinates(searchIncident)) {
        const [lat, lng] = searchIncident.split(",").map(Number);
        setIncidentLocation({ lat, lng });
        setSearchIncident("");
        return;
      }

      const trimmedQuery = String(searchIncident ?? "").trim();
      if (!trimmedQuery) return;

      const response = await geocodingSearch({ query: trimmedQuery });
      const payload = response?.data ?? response?.result ?? response;
      const bestResult = Array.isArray(payload) ? payload[0] : payload;

      const extractLocation = (value) => {
        if (Array.isArray(value)) {
          const firstItem = value[0];
          if (
            firstItem &&
            Number.isFinite(Number(firstItem.lat)) &&
            Number.isFinite(Number(firstItem.lng))
          ) {
            return {
              lat: Number(firstItem.lat),
              lng: Number(firstItem.lng),
            };
          }
          return null;
        }

        if (!value || typeof value !== "object") return null;

        if (
          Number.isFinite(Number(value.lat)) &&
          Number.isFinite(Number(value.lng))
        ) {
          return {
            lat: Number(value.lat),
            lng: Number(value.lng),
          };
        }

        const direct =
          value.location ??
          value.coordinates ??
          value.coords ??
          value.position ??
          value.geometry?.location;

        if (direct && typeof direct === "object") {
          return extractLocation(direct);
        }

        return null;
      };

      const location = extractLocation(bestResult ?? payload);

      if (location) {
        const lat = Number(
          location.lat ?? location.latitude ?? location.y ?? location[0],
        );
        const lng = Number(
          location.lng ?? location.longitude ?? location.lon ?? location[1],
        );

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setIncidentLocation({ lat, lng });
          setSearchIncident("");
          return;
        }
      }

      console.error(
        response?.message || "No se encontró la ubicación del incidente.",
      );
    } catch (error) {
      console.error("Error al buscar incidente con geocodificación:", error);
    }
  };

  const handlePlaceIncident = (location) => {
    setIncidentLocation(location);
    setSearchIncident("");
  };

  // Abre el modal con la coordenada del incidente como prellenado de referencia
  const handleOpenIncidentModal = (location) => {
    if (!location) return;

    const coordsText = `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
    setIncidentModalData({
      location: coordsText,
      raw: {
        incidentData: {
          location: coordsText,
        },
      },
    });
    setIsIncidentModalOpen(true);
  };

  const handleCloseIncidentModal = () => {
    setIsIncidentModalOpen(false);
    setIncidentModalData(null);
  };

  const getOSRMRoute = async (origin, destination) => {
    const response = await getRouteByPoints({
      lng_origen: origin.lng,
      lat_origen: origin.lat,
      lng_destino: destination.lng,
      lat_destino: destination.lat,
    });
    return response && response.routes && response.routes.length > 0
      ? response.routes[0]
      : null;
  };

  const fetchOSRMRoute = async (
    adjuster,
    customOffset = null,
    isInitial = false,
  ) => {
    if (!incidentLocation) return;

    try {
      const origin = { lat: adjuster.lat, lng: adjuster.lng };
      const route = await getOSRMRoute(origin, incidentLocation);

      if (route) {
        const coordinates = mapCoordinates(route.geometry);
        setSelectedRoute(coordinates);

        const distance = formatDistance(route.distance);
        const currentOSRMSeconds = route.duration;

        let activeOffset = trafficOffset;
        let activeInitialOSRM = initialDistance;

        if (customOffset !== null) {
          activeOffset = customOffset;
        }

        if (isInitial) {
          setInitialDistance(route.distance);
          setInitialOSRMDuration(currentOSRMSeconds);
        }

        let currentOffset = activeOffset;
        if (activeInitialOSRM > 0) {
          currentOffset = calculateRemainingOffset(
            route.distance,
            initialDistance,
            activeOffset,
          );
        }

        const eta = route.duration + currentOffset;

        lastAdjusterLocationRef.current = `${origin.lat},${origin.lng}`;

        setRouteInfo({
          distance: distance,
          duration: formatDuration(route.duration),
          durationInTraffic: formatDuration(eta),
          adjuster,
        });
      } else {
        console.error("No se pudo obtener la ruta de OSRM");
      }
    } catch (error) {
      console.error("Error al trazar la ruta con OSRM:", error);
    }
  };

  const getGoogleTrafficOffset = async (origin, destination) => {
    const directionsService = new window.google.maps.DirectionsService();

    try {
      const result = await new Promise((resolve, reject) => {
        directionsService.route(
          {
            origin: origin,
            destination: destination,
            travelMode: window.google.maps.TravelMode.DRIVING,
            drivingOptions: {
              departureTime: new Date(),
              trafficModel: "pessimistic",
            },
          },
          (res, status) => {
            if (status === "OK") resolve(res);
            else reject(status);
          },
        );
      });

      if (result && result.routes && result.routes[0]) {
        const leg = result.routes[0].legs[0];
        const normalSec = leg.duration?.value || 0;
        const trafficSec = leg.duration_in_traffic?.value || normalSec;

        return Math.max(0, trafficSec - normalSec);
      }
    } catch (err) {
      console.error(
        "Error al consultar Google Directions Service, usando offset = 0:",
        err,
      );
    }
    return 0;
  };

  const handleSelectAdjuster = async (adjuster) => {
    if (!incidentLocation) {
      console.warn("No hay ubicación del incidente seteada.");
      return;
    }

    const pos = parseLocation(adjuster.userLocation);
    if (!pos) {
      console.warn("Ubicación del ajustador inválida.");
      return;
    }

    let googleOffset = 0;
    if (window.google) {
      googleOffset = await getGoogleTrafficOffset(pos, incidentLocation);
    }

    setTrafficOffset(googleOffset);
    await fetchOSRMRoute(adjuster, googleOffset, true);
  };

  useEffect(() => {
    if (!routeInfo?.adjuster?.userId || !incidentLocation) {
      lastAdjusterLocationRef.current = null;
      return;
    }
    const currentAdjuster = data.find(
      (a) => String(a.userId) === String(routeInfo.adjuster.userId),
    );
    if (!currentAdjuster || !currentAdjuster.userLocation) return;
    const pos = parseLocation(currentAdjuster.userLocation);
    if (!pos) return;
    const locationKey = `${pos.lat},${pos.lng}`;
    if (lastAdjusterLocationRef.current === locationKey) return;

    lastAdjusterLocationRef.current = locationKey;
    fetchOSRMRoute({ ...currentAdjuster, ...pos });
  }, [
    data,
    routeInfo?.adjuster?.userId,
    incidentLocation,
    trafficOffset,
    initialDistance,
  ]);

  return (
    <Stack sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Contenedor principal de capas flotantes */}
      <Box
        sx={{
          position: "absolute",
          /* Bajas la posición 'top' en desktop (md: 70) para que no sea tapada por el navbar */
          top: { xs: 12, sm: 24, md: 90 },
          left: 0,
          right: 0,
          zIndex: 20,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 3 },
          pointerEvents: "none",
        }}
      >
        {/* Buscador de Coordenadas / Incidentes */}
        <Box
          sx={{
            zIndex: 1000,
            pointerEvents: "auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 1.5 },
            backgroundColor: "#FFFFFF",
            p: { xs: 1.25, sm: 1.5 },
            px: { xs: 1.5, sm: 2 },
            borderRadius: { xs: "16px", sm: "20px" },
            border: "1px solid #EAE8F5",
            boxShadow: "0px 10px 30px rgba(110, 107, 180, 0.12)",
            width: { xs: "100%", sm: "85%", md: "760px" },
            maxWidth: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <InputComponent
              label="Introduzca las coordenadas"
              value={searchIncident || ""}
              onChange={(val) => setSearchIncident(val)}
              textColor="#4A3B99"
              backgroundColor="#F8F7FC"
              width="100%"
              fontSize="14px"
            />
          </Box>

          <Box
            sx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "160px" },
              pt: { xs: 0.5, sm: 2.8 },
            }}
          >
            <ButtonComponent
              label="Buscar Incidente"
              onClick={handleSearchIncident}
              backgroundColor="#7B61FF"
              color="white"
              width="100%"
              style={{
                borderRadius: "12px",
                boxShadow: "0px 4px 12px rgba(123, 97, 255, 0.25)",
                fontWeight: 600,
                height: "38px",
              }}
            />
          </Box>
        </Box>

        {/* Fila de Terminal y Widget (Extremos en Desktop / Apilados ordenados en Mobile) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            justifyContent: "space-between",
            width: "100%",
            gap: { xs: 1.5, sm: 2 },
            pointerEvents: "none",
          }}
        >
          {/* Terminal alineada a la Izquierda */}
          {canSeeMonitor && (
            <Box
              sx={{
                pointerEvents: "auto",
                width: { xs: "100%", sm: "auto" },
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <ConnectionMonitorTerminal
                socket={socketWex}
                isConnected={isConnected}
                currentTime={currentTime}
                lastHeartbeat={lastHeartbeat}
                payload={payload}
                defaultCollapsed={true}
              />
            </Box>
          )}

          {/* Widget alineado a la Derecha */}
          <Box
            sx={{
              pointerEvents: "auto",
              width: { xs: "100%", sm: "auto" },
              display: "flex",
              justifyContent: "flex-end",
              ml: { sm: "auto" },
            }}
          >
            <ConnectionMonitorWidget
              isConnected={isConnected}
              lastHeartbeat={lastHeartbeat}
            />
          </Box>
        </Box>
      </Box>

      {/* Control de Tráfico */}
      <Box
        sx={{
          position: "absolute",
          zIndex: 10,
          bottom: { xs: 16, sm: 24 },
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(8px)",
          padding: { xs: "4px 14px", sm: "6px 20px" },
          borderRadius: "30px",
          border: "1px solid #EAE8F5",
          boxShadow: "0px 10px 25px rgba(110, 107, 180, 0.12)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <InputCheckboxes
          options={[{ label: "Ver Tráfico", value: "traffic" }]}
          value={showTraffic ? ["traffic"] : []}
          setValue={(val) => setShowTraffic(val.includes("traffic"))}
          color="#7B61FF"
          fontSize="13px"
        />
      </Box>

      {/* Mapa interactivo */}
      <MapDisplay
        receive={receive}
        selectedAgent={selectedAgent}
        data={filteredData}
        incidentLocation={incidentLocation}
        selectedRoute={selectedRoute}
        routeInfo={routeInfo}
        onSelectAdjuster={handleSelectAdjuster}
        onPlaceIncident={handlePlaceIncident}
        showTraffic={showTraffic}
        onOpenIncidentModal={handleOpenIncidentModal}
        permissions={permissions}
      />

      {canCreateIncident && (
        <CabinFormModal
        open={isIncidentModalOpen}
        onClose={handleCloseIncidentModal}
        initialData={incidentModalData}
        adjusters={adjusters}
        payload={payload}
        currentRegionId={payload?.regionIds}
        formState={incidentFormState}
        setFormState={setIncidentFormState}
        createForms={createForms}
        onFormSuccess={onIncidentCreated}
        onFormError={() => {}}
        alertRef={null}
        idSupervisor={payload?._id}
        prefillLocation={incidentLocation}
      />
      )}
      
    </Stack>
  );
}

export default RealTimeMaps;
